import { MODERATION_ACTION_TYPES, MODERATION_ENTITIES } from './moderationActionTypes';

const initialEntityState = {
  data: [],
  totalRecords: 0,
  totalPages: 0,
  status: 0,
  loading: false,
  error: null,
  filters: {
    page: 1,
    size: 10,
    searchTerm: "",
    sortBy: "createdAt",
    sortOrder: "desc"
  }
};

const initialEmployeesState = {
  list: [],
  loading: false,
  error: null,
  totalRecords: 0,
  totalPages: 0,
  filters: {
    page: 1,
    size: 10,
    searchTerm: "",
    status: 'active'
  }
};

const initialState = {
  [MODERATION_ENTITIES.PENDING_USERS]: { ...initialEntityState },
  [MODERATION_ENTITIES.APPROVED_USERS]: { ...initialEntityState },
  [MODERATION_ENTITIES.REJECTED_USERS]: { ...initialEntityState },
  [MODERATION_ENTITIES.REPORTS]: { ...initialEntityState },

  activeTab: MODERATION_ENTITIES.PENDING_USERS,
  employees: { ...initialEmployeesState },
};

const moderationReducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {

    case MODERATION_ACTION_TYPES.FETCH_MODERATION_LIST:
      return {
        ...state,
        [payload.entityType]: {
          ...state[payload.entityType],
          loading: true,
          data: [],
          status: 0,
          error: null,
          filters: {
            ...state[payload.entityType].filters,
            ...payload.filters
          }
        }
      };

    case MODERATION_ACTION_TYPES.FETCH_MODERATION_LIST_SUCCESS:
      return {
        ...state,
        [payload.entityType]: {
          ...state[payload.entityType],
          loading: false,
          data: payload.data.list || payload.data.data || [],
          totalRecords: payload.data.totalRecords || 0,
          totalPages: payload.data.totalPages || 0,
          status: payload.data.status || 200
        }
      };

    case MODERATION_ACTION_TYPES.FETCH_MODERATION_LIST_FAILURE:
      return {
        ...state,
        [payload.entityType]: {
          ...state[payload.entityType],
          loading: false,
          error: payload.error,
          status: payload.error?.status || 400
        }
      };

    // User approval actions
    case MODERATION_ACTION_TYPES.APPROVE_USER_REQUEST:
    case MODERATION_ACTION_TYPES.REJECT_USER_REQUEST:
      return {
        ...state,
        [MODERATION_ENTITIES.PENDING_USERS]: {
          ...state[MODERATION_ENTITIES.PENDING_USERS],
          loading: true
        },
        [MODERATION_ENTITIES.APPROVED_USERS]: {
          ...state[MODERATION_ENTITIES.APPROVED_USERS],
          loading: true
        }
      };

    case MODERATION_ACTION_TYPES.APPROVE_USER_SUCCESS:
      return {
        ...state,
        [MODERATION_ENTITIES.PENDING_USERS]: {
          ...state[MODERATION_ENTITIES.PENDING_USERS],
          loading: false,
          data: state[MODERATION_ENTITIES.PENDING_USERS].data.filter(user => user.userId !== payload.requestId)
        }
        // APPROVED_USERS loading stays true — cleared by FETCH_MODERATION_LIST_SUCCESS after fetchApprovedUsers
      };

    case MODERATION_ACTION_TYPES.REJECT_USER_SUCCESS:
      return {
        ...state,
        [MODERATION_ENTITIES.PENDING_USERS]: {
          ...state[MODERATION_ENTITIES.PENDING_USERS],
          loading: false,
          data: state[MODERATION_ENTITIES.PENDING_USERS].data.filter(user => user.userId !== payload.requestId)
        },
        [MODERATION_ENTITIES.REJECTED_USERS]: {
          ...state[MODERATION_ENTITIES.REJECTED_USERS],
          loading: false
        }
      };

    case MODERATION_ACTION_TYPES.APPROVE_USER_FAILURE:
    case MODERATION_ACTION_TYPES.REJECT_USER_FAILURE:
      return {
        ...state,
        [MODERATION_ENTITIES.PENDING_USERS]: {
          ...state[MODERATION_ENTITIES.PENDING_USERS],
          loading: false,
          error: payload.error
        },
        [MODERATION_ENTITIES.APPROVED_USERS]: {
          ...state[MODERATION_ENTITIES.APPROVED_USERS],
          loading: false
        }
      };
    case MODERATION_ACTION_TYPES.FETCH_EMPLOYEES_LIST:
      return {
        ...state,
        employees: {
          ...state.employees,
          loading: true,
          error: null,
          filters: {
            ...state.employees.filters,
            ...action.payload.filters
          }
        }
      };

    case MODERATION_ACTION_TYPES.FETCH_EMPLOYEES_LIST_SUCCESS: {
      const { result } = payload;
      const newList = result?.data?.users || result?.users || [];
      const currentPage = state.employees.filters.page;

      return {
        ...state,
        employees: {
          ...state.employees,
          loading: false,
          list: currentPage === 1 ? newList : [...state.employees.list, ...newList],
          totalRecords: result?.totalRecords || result?.data?.totalRecords || 0,
          totalPages: result?.totalPages || result?.data?.totalPages || 0
        }
      };
    }

    case MODERATION_ACTION_TYPES.FETCH_EMPLOYEES_LIST_FAILURE:
      return {
        ...state,
        employees: {
          ...state.employees,
          loading: false,
          error: payload.error
        }
      };

    case MODERATION_ACTION_TYPES.SET_ACTIVE_MODERATION_TAB:
      const tabId = payload.tabNameToRefresh;
      return {
        ...state,
        ...(payload.shouldRefresh
          ? {
            [tabId]: {
              ...state[tabId],
              shouldRefresh: payload.shouldRefresh,
              data: {
                ...state[tabId].data,
                users: state[tabId].data?.users?.map(user =>
                  user.userId === payload.userId
                    ? { ...user, assignedEmployee: payload?.empName }
                    : user
                ),
              }
            },
          }
          : {}),
        activeTab: payload.tabName,
      };

    case MODERATION_ACTION_TYPES.SET_MODERATION_FILTERS:
      return {
        ...state,
        [payload.entityType]: {
          ...state[payload.entityType],
          filters: {
            ...state[payload.entityType].filters,
            ...payload.filters
          }
        }
      };

    case MODERATION_ACTION_TYPES.RESET_MODERATION_STATE:
      return {
        ...state,
        [payload.entityType]: initialEntityState
      };

    case MODERATION_ACTION_TYPES.UPDATE_MODERATION_ROW:
      return {
        ...state,
        [payload.entityType]: {
          ...state[payload.entityType],
          loading: true,
          error: null
        }
      };

    case MODERATION_ACTION_TYPES.UPDATE_MODERATION_ROW_FAILURE:
      return {
        ...state,
        [payload.entityType]: {
          ...state[payload.entityType],
          loading: false,
          error: payload.error
        }
      };

    case MODERATION_ACTION_TYPES.UPDATE_MODERATION_ROW_SUCCESS:
      return {
        ...state,
        [payload.entityType]: {
          ...state[payload.entityType],
          loading: false,
          data: {
            ...state[payload.entityType].data,
            users: state[payload.entityType].data?.users?.map(user =>
              user.id === payload.rowId || user.userId === payload.rowId
                ? { ...user, ...payload.result }
                : user
            ),
          },
        },
      };

    case MODERATION_ACTION_TYPES.VALUE_APPENT_IN_ARRAY: {
      return {
        ...state,
        valueAddInList: action.valueAddInList
      };
    };
    case MODERATION_ACTION_TYPES.REPORT_MODERATION_LOADER: {
      return {
        ...state,
        loaderreportMod: action.loaderreportMod,
        loading: false
      };
    };
    case MODERATION_ACTION_TYPES.REPORT_MODERATION_LIST: {
      return {
        ...state,
        reportModertionList: action.reportModertionList,
        loading: false
      };
    };

    default:
      return state;
  }
};

export default moderationReducer;
